import React, { useState } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Alert, 
  Badge,
  Button,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { AccountClient, UpdateRoleDto, AddMemberDto } from '../api/api-client';
import { useAccountMembers } from '../hooks/useAccount';
import confirm from './ConfirmModal';

export const MemberManagement: React.FC = () => {
  const { data: members = [], isLoading: loading, refetch } = useAccountMembers();
  const [saving, setSaving] = useState(false);
  
  // Add member modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  
  // Available roles
  const roles = [
    { value: 'viewer', label: 'Viewer', description: 'Can only view account info' },
    { value: 'member', label: 'Member', description: 'Can edit content and view account info' },
    { value: 'admin', label: 'Admin', description: 'Can manage members and API keys' },
    { value: 'owner', label: 'Owner', description: 'Full account access' }
  ];


  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) return;
    
    try {
      setSaving(true);
      const client = new AccountClient();
      const addMemberDto: AddMemberDto = {
        email: newMemberEmail,
        role: newMemberRole
      };
      
      await client.addMember(addMemberDto);
      
      // Reset form and close modal
      setNewMemberEmail('');
      setNewMemberRole('member');
      setShowAddModal(false);
      
      // Reload members
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (memberId: number, memberEmail: string, newRole: string) => {
    try {
      setSaving(true);
      const client = new AccountClient();
      const updateDto: UpdateRoleDto = { role: newRole };
      
      await client.updateMemberRole(memberId, updateDto);
      
      // Reload members to get updated data
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (memberId: number, memberEmail: string) => {
    const confirmed = await confirm({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${memberEmail} from the account? This action cannot be undone.`,
      confirmText: 'Remove',
      confirmColor: 'danger'
    });

    if (!confirmed) return;

    try {
      setSaving(true);
      const client = new AccountClient();
      await client.removeMember(memberId);
      
      // Reload members
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'owner': return 'primary';
      case 'admin': return 'success';
      case 'member': return 'info';
      case 'viewer': return 'secondary';
      default: return 'light';
    }
  };

  const getStatusBadge = (hasCompletedSetup: boolean) => {
    return hasCompletedSetup ? (
      <Badge color="success">Active</Badge>
    ) : (
      <Badge color="warning">Setup Pending</Badge>
    );
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading members...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={10} lg={8} className="mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Manage Members</h2>
            <Button 
              color="primary" 
              onClick={() => setShowAddModal(true)}
              disabled={saving}
            >
              <i className="fas fa-plus me-2"></i>
              Add Member
            </Button>
          </div>
          
          <Card>
            <CardHeader>
              <h5 className="mb-0">Account Members ({members.length})</h5>
            </CardHeader>
            <CardBody>
              {members.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.id}>
                        <td>{member.email || ''}</td>
                        <td>
                          <Badge color={getRoleBadgeColor(member.accountRole || '')}>
                            {member.accountRole || ''}
                          </Badge>
                        </td>
                        <td>
                          {getStatusBadge(member.hasCompletedSetup || false)}
                        </td>
                        <td>
                          {member.accountRole !== 'owner' ? (
                            <UncontrolledDropdown className="d-inline-block me-2">
                              <DropdownToggle 
                                caret 
                                size="sm" 
                                color="outline-secondary"
                                disabled={saving}
                              >
                                Change Role
                              </DropdownToggle>
                              <DropdownMenu>
                                {roles.map((role) => (
                                  <DropdownItem
                                    key={role.value}
                                    onClick={() => handleUpdateRole(member.id || 0, member.email || '', role.value)}
                                    disabled={role.value === member.accountRole}
                                  >
                                    <div>
                                      <strong>{role.label}</strong>
                                      <br />
                                      <small className="text-muted">{role.description}</small>
                                    </div>
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          ) : (
                            <Badge color="primary" className="me-2">Owner</Badge>
                          )}
                          
                          {member.accountRole !== 'owner' && (
                            <Button 
                              size="sm" 
                              color="outline-danger"
                              onClick={() => handleRemoveMember(member.id || 0, member.email || '')}
                              disabled={saving}
                            >
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-users fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No members found</h5>
                  <p className="text-muted">Invite team members to collaborate on your account.</p>
                </div>
              )}
            </CardBody>
          </Card>
          
          <Alert color="success" className="mt-4">
            <h6>Member Management</h6>
            <p className="mb-0">
              <strong>Available features:</strong>
            </p>
            <ul className="mt-2 mb-0">
              <li>✅ View account members and their roles</li>
              <li>✅ Change member roles (for non-owners)</li>
              <li>✅ Add new members (pre-create for Google auth)</li>
              <li>✅ Remove members from account</li>
            </ul>
          </Alert>
        </Col>
      </Row>

      {/* Add Member Modal */}
      <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)}>
        <ModalHeader toggle={() => setShowAddModal(false)}>
          Add New Member
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="mb-3">
            <small>
              <strong>Note:</strong> This will pre-create a user account that can be activated 
              when the user signs in with Google using this email address.
            </small>
          </Alert>
          
          <Form onSubmit={(e) => { e.preventDefault(); handleAddMember(); }}>
            <FormGroup>
              <Label for="memberEmail">Email Address</Label>
              <Input
                type="email"
                id="memberEmail"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={saving}
              />
            </FormGroup>
            
            <FormGroup>
              <Label for="memberRole">Role</Label>
              <Input
                type="select"
                id="memberRole"
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value)}
                disabled={saving}
              >
                {roles.filter(role => role.value !== 'owner').map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="secondary" 
            onClick={() => setShowAddModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleAddMember}
            disabled={saving || !newMemberEmail.trim()}
          >
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Add Member
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};